import { PrintableDocumentsDisplayName } from "@redotech/redo-fulfillment-group-common/fulfillment-print-status";
import { PrintableDocument } from "@redotech/redo-fulfillment-group-common/print-job";
import { RedoHorizontalTab } from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";

export const ALWAYS_SHOWN_PRINT_MODAL_DOCUMENTS = [
  PrintableDocument.PACKING_SLIP,
  PrintableDocument.PICK_LIST,
  PrintableDocument.ORDER_SUMMARY,
] as const;
export type AlwaysShownPrintModalDocument =
  (typeof ALWAYS_SHOWN_PRINT_MODAL_DOCUMENTS)[number];

export const ALWAYS_SHOWN_PRINT_MODAL_DOCUMENT_LABELS: Record<
  AlwaysShownPrintModalDocument,
  string
> = {
  [PrintableDocument.PACKING_SLIP]: "Packing Slip",
  [PrintableDocument.PICK_LIST]: "Pick List",
  [PrintableDocument.ORDER_SUMMARY]: "Order Summary",
};

export interface DocumentPrintStatus {
  needsPrinting: boolean;
  /** Only meaningful on the PERSONALIZED_SLIP entry: a slip was generated. */
  hasGeneratedInsert?: boolean;
}

export type DocumentPrintStatusMap = Record<
  AlwaysShownPrintModalDocument,
  DocumentPrintStatus
> &
  Partial<Record<PrintableDocument, DocumentPrintStatus>>;

/** The document state each tab's `shouldShow` reasons over to decide rendering. */
export interface PrintModalTabContext {
  openedDocument: PrintableDocument;
  documentPrintStatusMap: DocumentPrintStatusMap;
}

interface PrintModalTabSpec {
  key: PrintableDocument;
  label: string;
  shouldShow: (ctx: PrintModalTabContext) => boolean;
}

// Each tab declares its own render condition, so adding a conditional tab is one
// entry here rather than a new positional flag threaded through getDocumentTabs.
const PRINT_MODAL_TAB_SPECS: PrintModalTabSpec[] = [
  {
    key: PrintableDocument.PERSONALIZED_SLIP,
    label: PrintableDocumentsDisplayName[PrintableDocument.PERSONALIZED_SLIP],
    shouldShow: (ctx) =>
      ctx.documentPrintStatusMap[PrintableDocument.PERSONALIZED_SLIP]
        ?.hasGeneratedInsert ?? false,
  },
  ...ALWAYS_SHOWN_PRINT_MODAL_DOCUMENTS.map((document) => ({
    key: document,
    label: ALWAYS_SHOWN_PRINT_MODAL_DOCUMENT_LABELS[document],
    shouldShow: () => true,
  })),
];

export function getDocumentTabs(
  ctx: PrintModalTabContext,
): RedoHorizontalTab<PrintableDocument>[] {
  const tabs: RedoHorizontalTab<PrintableDocument>[] = [];
  const seen = new Set<PrintableDocument>();
  const labelFor = (key: PrintableDocument): string =>
    PRINT_MODAL_TAB_SPECS.find((spec) => spec.key === key)?.label ??
    PrintableDocumentsDisplayName[key];
  const addTab = (key: PrintableDocument) => {
    if (!seen.has(key)) {
      seen.add(key);
      tabs.push({ key, label: labelFor(key) });
    }
  };

  // Opened document shows first, but if it has its own spec, defer to that
  // condition (so a conditional tab like the insert stays hidden until it holds).
  const openedSpec = PRINT_MODAL_TAB_SPECS.find(
    (spec) => spec.key === ctx.openedDocument,
  );
  if (!openedSpec || openedSpec.shouldShow(ctx)) {
    addTab(ctx.openedDocument);
  }
  for (const spec of PRINT_MODAL_TAB_SPECS) {
    if (spec.shouldShow(ctx)) {
      addTab(spec.key);
    }
  }

  return tabs;
}
